// src/clips/ClipLibrary.js
// Global singleton storing all ClipDefinition objects.
// Each ClipDefinition is { id, name, notes: [{time,duration,pitch,velocity}] }
// Notes are normalized so time=0 is the clip start. Duration is computed
// from the furthest note end.

let _nextClipId = 1;
function generateClipId() {
  return `clip-${_nextClipId++}-${Math.random().toString(36).slice(2, 6)}`;
}

export class ClipLibrary {
  constructor() {
    this.definitions = [];
    this._listeners = new Set();
  }

  onChange(fn) {
    this._listeners.add(fn);
    return () => this._listeners.delete(fn);
  }

  _emit() {
    this._listeners.forEach((fn) => fn(this));
  }

  /** Compute clip duration from its notes (max note end). */
  static getDuration(notes) {
    if (!notes || notes.length === 0) return 0.001;
    return Math.max(...notes.map((n) => n.time + n.duration), 0.001);
  }

  addClip(name, notes) {
    const def = {
      id: generateClipId(),
      name: name || 'Untitled clip',
      notes: (notes || []).map((n) => ({
        id: n.id || `cn-${Math.random().toString(36).slice(2, 9)}`,
        time: n.time, duration: n.duration,
        pitch: n.pitch, velocity: n.velocity
      }))
    };
    this.definitions.push(def);
    this._emit();
    return def;
  }

  removeClip(id) {
    const idx = this.definitions.findIndex((d) => d.id === id);
    if (idx < 0) return false;
    this.definitions.splice(idx, 1);
    this._emit();
    return true;
  }

  getClip(id) {
    return this.definitions.find((d) => d.id === id) || null;
  }

  renameClip(id, name) {
    const def = this.getClip(id);
    if (!def) return false;
    def.name = name;
    this._emit();
    return true;
  }

  duplicateClip(id) {
    const orig = this.getClip(id);
    if (!orig) return null;
    const dup = this.addClip(
      `Copy of ${orig.name}`,
      (orig.notes || []).map((n) => ({ ...n }))
    );
    return dup;
  }

  /** Update a note inside a clip definition by note ID. */
  updateClipNote(clipId, noteId, partial) {
    const def = this.getClip(clipId);
    if (!def) return false;
    const note = def.notes.find((n) => n.id === noteId);
    if (!note) return false;
    Object.assign(note, partial);
    this._emit();
    return true;
  }

  /** Remove a note from a clip definition by note ID. */
  removeClipNote(clipId, noteId) {
    const def = this.getClip(clipId);
    if (!def) return false;
    const idx = def.notes.findIndex((n) => n.id === noteId);
    if (idx < 0) return false;
    def.notes.splice(idx, 1);
    this._emit();
    return true;
  }

  /** Add a note to a clip definition. */
  addClipNote(clipId, note) {
    const def = this.getClip(clipId);
    if (!def) return false;
    const newNote = {
      id: `cn-${Math.random().toString(36).slice(2, 9)}`,
      time: note.time || 0,
      duration: note.duration || 0.5,
      pitch: note.pitch || 60,
      velocity: note.velocity != null ? note.velocity : 0.8
    };
    def.notes.push(newNote);
    this._emit();
    return newNote;
  }
}

export const clipLibrary = new ClipLibrary();
